import * as React from 'react'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { FaIcon, faPlus, faTrash } from '@/lib/fa'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import SearchColor from '@/assets/icons_new/SearchColor.svg'
import AlimentationRowItem from './AlimentationRowItem'

interface BusinessTripEditAlimentationProps {
  tripData: any
  onUpdate: (field: string, value: any) => void
  onUpdateArrayItem?: (arrayName: string, index: number, updates: any) => void
  onAddArrayItem?: (arrayName: string, newItem: any) => void
  onRemoveArrayItems?: (arrayName: string, indices: number[]) => void
  companyId: string
  validationErrors?: Record<string, any>
}


const alimentationColumnKeys = [
  { titleKey: 'businessTrip.col-country', key: 'country', type: 'string', sort: false },
  { titleKey: 'businessTrip.col-duration', key: 'tripLength', type: 'string', sort: false },
  { titleKey: 'businessTrip.col-diet-count', key: 'dietQuantity', type: 'string', sort: false },
  { titleKey: 'businessTrip.col-breakfasts', key: 'breakfasts', type: 'number', sort: false },
  { titleKey: 'businessTrip.col-dinners', key: 'dinners', type: 'number', sort: false },
  { titleKey: 'businessTrip.col-suppers', key: 'suppers', type: 'number', sort: false },
  { titleKey: '', key: 'breakfastInParticle', type: 'boolean', sort: false },
  { titleKey: '', key: 'dinnerInParticle', type: 'boolean', sort: false },
  { titleKey: '', key: 'supperInParticle', type: 'boolean', sort: false },
] as const

const BusinessTripEditAlimentation: React.FC<BusinessTripEditAlimentationProps> = ({
  tripData,
  onUpdate,
  onUpdateArrayItem,
  onAddArrayItem,
  onRemoveArrayItems,
  companyId,
}) => {
  const { t } = useLocalize()
  const AlimentationColumns: TableHeaderColumn[] = React.useMemo(() =>
    alimentationColumnKeys.map(({ titleKey, ...rest }) => ({
      ...rest,
      title: titleKey ? t(titleKey) : '',
    })),
    [t],
  )
  const [searchValue, setSearchValue] = React.useState('')
  const [selectedIds, setSelectedIds] = React.useState<number[]>([])
  const [sortConfig, setSortConfig] = React.useState<{ key: string; direction: 'asc' | 'desc' } | null>(null)
  const [showSearch, setShowSearch] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)

  // Click outside to close search
  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setSearchValue('')
        setShowSearch(false)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  const allowances = tripData?.allowances || []
  const alimentations = tripData?.alimentations || []

  const filteredAlimentations = React.useMemo(() => {
    if (!searchValue) return alimentations
    const search = searchValue.toLowerCase()
    return alimentations.filter((al: any) => {
      return (
        al.country?.name?.toLowerCase().includes(search) ||
        al.dietQuantity?.toLowerCase().includes(search)
      )
    })
  }, [alimentations, searchValue])

  const getOriginalIndex = (al: any): number => {
    const idx = alimentations.indexOf(al)
    if (idx !== -1) return idx
    return alimentations.findIndex((item: any) => {
      if (item.id != null && al.id != null && String(item.id) === String(al.id)) return true
      if (item.lp != null && al.lp != null && item.lp !== -1 && al.lp !== -1 && item.lp === al.lp) return true
      return false
    })
  }

  const toggleAll = (checked: boolean) => {
    if (checked) {
      const indices = filteredAlimentations
        .map((al: any) => getOriginalIndex(al))
        .filter((i) => i !== -1)
      setSelectedIds(indices)
    } else {
      setSelectedIds([])
    }
  }

  const toggleId = (id: number) => {
    if (id === -1) return
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const handleHeaderClick = (key: string) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      }
      return { key, direction: 'asc' }
    })
  }

  const handleAdd = () => {
    if (onAddArrayItem) {
      onAddArrayItem('alimentations', {
        lp: -1,
        country: null,
        tripLenght: '',
        dietQuantity: '',
        breakfasts: 0,
        breakfastInParticle: false,
        dinners: 0,
        dinnerInParticle: false,
        suppers: 0,
        supperInParticle: false,
      })
    }
  }

  const handleDelete = () => {
    if (selectedIds.length > 0) {
      const indicesToRemove = new Set(selectedIds)
      const updated = alimentations.filter((_: any, i: number) => !indicesToRemove.has(i))
      onUpdate('alimentations', updated)
      setSelectedIds([])
    }
  }

  const isSelectedAny = selectedIds.length > 0

  const buildUpdateField = (al: any) => (field: string, value: any) => {
    if (!onUpdateArrayItem) return
    const originalIndex = getOriginalIndex(al)
    if (originalIndex === -1) return
    onUpdateArrayItem('alimentations', originalIndex, { [field]: value })
    const allowanceIndex = allowances.findIndex((a: any) => a.lp === al.lp)
    if (allowanceIndex !== -1) {
      onUpdateArrayItem('allowances', allowanceIndex, { [field]: value })
    }
  }

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-rounded-lg tw-bg-white">
      {/* Action Bar */}
      <div className="tw-flex tw-w-full tw-shrink-0 tw-items-center tw-justify-between tw-border-b tw-border-[#e5e6e6] tw-bg-[#fafbff] tw-px-4 tw-py-3">
        {/* Left side - Actions */}
        <div className="tw-flex tw-items-center tw-gap-4">
          {/* Search button with dropdown */}
          <div className="tw-relative" ref={containerRef}>
            <div
              className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-border-2 tw-px-1 ${
                showSearch ? 'tw-rounded-lg tw-border-primary' : 'tw-border-transparent'
              }`}
              onClick={() => setShowSearch((prev) => !prev)}
            >
              <img src={SearchColor} alt="Search Icon" />
              <p className="tw-text-sm tw-font-medium tw-text-primary">
                {t('operation.find') || 'Wyszukaj'}
              </p>
            </div>
            {showSearch && (
              <div className="tw-absolute tw-z-50 tw-mt-2 tw-w-[320px] tw-rounded-lg tw-bg-white tw-p-2 tw-shadow-md">
                <div className="tw-relative tw-w-full">
                  <input
                    autoFocus
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder={t('operation.find') || 'Szukaj...'}
                    className="tw-w-full tw-rounded tw-border tw-border-neutral-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div className="tw-absolute tw-right-2 tw-top-1/2 tw-flex tw--translate-y-1/2 tw-items-center tw-gap-2">
                    {searchValue && (
                      <>
                        <span className="tw-text-sm tw-text-gray-500">
                          {filteredAlimentations.length}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setSearchValue('')
                          }}
                          className="tw-text-gray-400 hover:tw-text-gray-700"
                        >
                          ✕
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Add button */}
          <div
            className="tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6] tw-cursor-pointer"
            onClick={handleAdd}
            title={t('answer.add') || 'Dodaj'}
          >
            <FaIcon icon={faPlus} className="tw-h-4 tw-w-4 tw-text-[#2448bb]" />
            <span className="tw-text-sm tw-font-medium tw-text-[#2448bb]">
              {t('answer.add') || 'Dodaj'}
            </span>
          </div>

          {/* Delete button */}
          {isSelectedAny && (
            <div
              className="tw-flex tw-h-5 tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 hover:tw-bg-[#e5e6e6] tw-cursor-pointer"
              onClick={handleDelete}
              title={t('operation.delete')}
            >
              <FaIcon icon={faTrash} className="tw-h-4 tw-w-4 tw-text-error" />
              <span className="tw-text-sm tw-font-medium tw-text-error">
                {t('operation.delete')} ({selectedIds.length})
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto tw-border-t tw-border-base-300">
        <table className="tw-w-full tw-table-auto tw-border-collapse">
          <TableHeader
            columns={AlimentationColumns}
            fontSize="md"
            isSelectedAny={isSelectedAny}
            rounded
            sortConfig={sortConfig}
            bolder
            disabledToggle={false}
            toggleAll={toggleAll}
            onHeaderClick={handleHeaderClick}
          />
          <tbody>
            {filteredAlimentations.length > 0 ? (
              filteredAlimentations.map((al: any, index: number) => {
                const originalIndex = getOriginalIndex(al)
                const isChecked = selectedIds.includes(originalIndex)
                return (
                  <AlimentationRowItem
                    key={
                      originalIndex !== -1 ? originalIndex : `temp-${index}`
                    }
                    al={al}
                    isChecked={isChecked}
                    toggleId={toggleId}
                    originalIndex={originalIndex}
                    onUpdateField={buildUpdateField(al)}
                  />
                )
              })
            ) : (
              <tr>
                <td colSpan={AlimentationColumns.length + 1} className="tw-p-8 tw-text-center">
                  <p className="tw-text-content-base tw-opacity-70">
                    {t('businessTrip.no-data')}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default BusinessTripEditAlimentation
